import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import {
  VideoCameraOutlined,
  ArrowLeftOutlined,
  MoneyCollectOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  BankOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleTwoTone,
  QrcodeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  Card,
  Descriptions,
  Table,
  message,
  Button,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
  Empty,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const ShowDetail = () => {
  const { showId, dateId, timeslotId } = useParams();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [showInfo, setShowInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchShowBookings(pagination.current, search);
  }, [showId, dateId, timeslotId, pagination.current, search]);

  const fetchShowBookings = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      let url = `booking/theatre-show-bookings/${showId}/${dateId}/${timeslotId}/`;
      const params = { page, page_size: pagination.pageSize };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axiosInstance.get(url, { params });

      const data = response.data;
      const results = data.results || [];

      if (results.length > 0) {
        const firstBooking = results[0];
        setShowInfo({
          movie: firstBooking.show.movie,
          movie_lang: firstBooking.show.movie_lang,
          theatre: firstBooking.show.theatre,
          screen: firstBooking.show.screen,
          date: firstBooking.show_time_slot.date,
          timeslot_title: firstBooking.show_time_slot.title,
          start_time: firstBooking.show_time_slot.start,
          end_time: firstBooking.show_time_slot.end,
        });
      }

      setBookings(results);
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        total: data.count || 0,
      });
    } catch (error) {
      console.error(error);
      message.error('Failed to load bookings for this show');
      setBookings([]);
      setShowInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || 10,
    }));
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined style={{ marginRight: 6 }} />
            <strong>{record.booked_by.name || record.booked_by_email}</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            {record.booked_by.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Seats Booked',
      key: 'seats_count',
      align: 'center',
      render: (_, record) => (
        <Tag color="blue" style={{ fontWeight: 'bold' }}>
          {record.number_of_seats} seat{record.number_of_seats > 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Seat Details',
      key: 'seats',
      width: 300,
      render: (_, record) => (
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: '13px' }}>
          {record.seats.map((seat, i) => (
            <li key={i}>
              <strong>{seat.section}</strong> → {seat.block} → Row {seat.row}
              {seat.position ? ` (Pos ${seat.position})` : ''} → Seat <strong>{seat.number}</strong>
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: 'Paid Status',
      key: 'paid',
      align: 'center',
      render: (_, record) => (
        <Tag
          color={record.paid ? 'green' : 'red'}
          icon={record.paid ? <CheckCircleOutlined /> : <ClockCircleTwoTone />}
        >
          {record.paid ? 'Paid' : 'Unpaid'}
        </Tag>
      ),
    },
    {
      title: 'Booking Type',
      key: 'reserved',
      align: 'center',
      render: (_, record) => (
        <Tag color={record.is_reserved ? 'purple' : 'geekblue'}>
          {record.is_reserved ? 'Reserved' : 'Booked'}
        </Tag>
      ),
    },
    {
      title: 'QR Verified',
      key: 'verified',
      align: 'center',
      render: (_, record) => (
        <Tag icon={<QrcodeOutlined />} color={record.is_verified ? 'green' : 'orange'}>
          {record.is_verified ? 'Verified' : 'Not Verified'}
        </Tag>
      ),
    },
    {
      title: 'Booked At',
      key: 'created_at',
      render: (_, record) => (
        <Text type="secondary">
          {new Date(record.created_at).toLocaleString('en-IN')}
        </Text>
      ),
    },
  ];

  if (loading && bookings.length === 0) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div className="dashboard">
      <PageHeading name="Show Bookings" icon={<VideoCameraOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/theater/shows')}
        style={{ marginBottom: 16 }}
      >
        Back to Show List
      </Button>

      {/* Show Summary */}
      {showInfo && (
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={16}>
            <Card title="Show Details" bordered={false}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Movie">
                  <strong>{showInfo.movie}</strong> ({showInfo.movie_lang})
                </Descriptions.Item>
                <Descriptions.Item label="Theatre">
                  {showInfo.theatre}
                </Descriptions.Item>
                <Descriptions.Item label={<><BankOutlined /> Screen</>}>
                  {showInfo.screen}
                </Descriptions.Item>
                <Descriptions.Item label={<><CalendarOutlined /> Date</>}>
                  {new Date(showInfo.date).toLocaleDateString('en-IN')}
                </Descriptions.Item>
                <Descriptions.Item label="Time Slot">
                  <strong>{showInfo.timeslot_title}</strong>
                </Descriptions.Item>
                <Descriptions.Item label={<><ClockCircleOutlined /> Show Time</>}>
                  <strong>{showInfo.start_time} - {showInfo.end_time}</strong>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Booking Summary" bordered={false}>
              <Descriptions column={1}>
                <Descriptions.Item label="Total Bookings">
                  <Title level={3} style={{ margin: 0 }}>{pagination.total}</Title>
                </Descriptions.Item>
                <Descriptions.Item label="Total Seats Booked">
                  <Title level={3} style={{ margin: 0 }}>
                    {bookings.reduce((sum, b) => sum + b.number_of_seats, 0)}
                  </Title>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}

      {/* Search Bar — EXACTLY like ShowList */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by Booking ID, Name, Email or Seat Number"
          prefix={<SearchOutlined />}
          style={{ width: '320px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Showing <strong>{bookings.length}</strong> of <strong>{pagination.total}</strong> bookings
        </Text>
      </div>

      {bookings.length === 0 ? (
        <Empty description="No bookings yet for this show time" />
      ) : (
        <Table
          dataSource={bookings}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: false, // ← No "Go to page"
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bookings`,
            onChange: handlePageChange,
            onShowSizeChange: (current, size) => handlePageChange(current, size),
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No bookings found' }}
        />
      )}
    </div>
  );
};

export default ShowDetail;